import { useTranslation } from 'react-i18next';
import Button from '../../../components/buttons/Button';
import SectionHeader from '../../../components/SectionHeader';
import { ISpecificationItem } from '../../../modules/vehicleModels/types/interfaces';
import { Fragment, useMemo, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandAccordion from '../../../modules/products/components/productForm/ExpandAccordion';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import { Form, Formik } from 'formik';
import SwitchActivation from '../../../components/layout/SwitchActivation';
import { useQuery } from '@tanstack/react-query';
import { generalGet } from '../../../API/api';
import FormSkeleton from '../../../components/loaders/FormSkeleton';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import { useParams } from 'react-router-dom';

export default function VehicleModelSpecsEdit({
  data,
  setIsEditing,
  variant
}: {
  data: ISpecificationItem[];
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  variant?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { modelId, variantId } = useParams();
  const [expanded, setExpanded] = useState(0);

  const { data: specsDataApi, isLoading } = useQuery({
    queryKey: ['spec-groups'],
    queryFn: () => generalGet('/admin/specs-groups?specs=1'),
  });

  const specsData = useMemo(() => {
    const reqData = specsDataApi?.data?.data;
    if (reqData) {
      return reqData
        ?.flatMap((specsGroup: any) => {
          if (!specsGroup?.specs?.length) return null;
          return {
            ...specsGroup,
            specs: specsGroup.specs?.map((spec: any) => {
              const oldSpecData: any = data?.find((ele: any) => ele?.spec?.id === spec.id);
              return {
                ...spec,
                value: spec.type === 'checkbox' ? oldSpecData?.value?.en || '' : oldSpecData?.value || { en: '', ar: '' },
                featured: oldSpecData?.featured || 0
              };
            })
          };
        })
        .filter(Boolean);
    }
  }, [specsDataApi]);

  const { handleSubmit, submitLoading } = useFormsIntegrationHelpers({
    id: variant ? variantId : modelId,
    editApi: `admin/${variant ? "variants": "models"}/${variant ? variantId : modelId}/spec-value`,
    editPut: true,
    itemName: t('specifications'),
    queryKey: ['specifications']
  });

  if (isLoading) return <FormSkeleton />;

  return (
    <SectionHeader
      title={t('specifications')}
      customStyle="last specs_section"
      rightComp={
        <div className="buttons">
          <Button onClick={() => setIsEditing(false)}>
            <span className="bold">{t('specifications_list')}</span>
          </Button>
        </div>
      }
    >
      <Formik
        initialValues={
          {
            specs: specsData?.flatMap((specsGroup: any) => specsGroup.specs)
          } as {
            specs: {
              id: number;
              name: string;
              type: string;
              featured: boolean;
              value:
                | {
                    en: string;
                    ar: string;
                  }
                | string;
            }[];
          }
        }
        enableReinitialize
        validateOnChange={false}
        onSubmit={(values) => {
          handleSubmit({
            specs: values?.specs
              ?.map((item: any) => {
                let keys = {}
                if((item?.type === 'checkbox' && item?.value) || (item?.type === 'text' && (item?.value?.en || item?.value?.ar))){
                  keys = {
                    value: item?.value,
                    featured: item.featured || 0
                  }
                } else if(!data?.some((ele: any) => ele?.spec?.id === item.id)) {
                  return null;
                }
                return {
                  id: item.id,
                  ...keys,
                  featured: item.featured || 0 //TODO: Remove after backend deploy new updates
                }
              }).filter(Boolean)
          }, undefined, undefined, true);
        }}
      >
        {(formik) => {
          let lastIndex = -1;
          return (
            <Form>
              <div className="accordions-container" style={{ marginBottom: '2rem' }}>
                {specsData?.map((group: any, index: any) => (
                  <Accordion
                    expanded={expanded === index}
                    onChange={(e) => {
                      e.stopPropagation();
                      setExpanded(expanded === index ? -1 : index);
                    }}
                  >
                    <AccordionSummary
                      sx={{
                        '&.Mui-focusVisible': {
                          backgroundColor: '#000'
                        }
                      }}
                      aria-controls="panel1a-content"
                      id="panel1a-header"
                      className={`header-container ${expanded !== index && 'expand'}`}
                    >
                      <p>
                        <ExpandAccordion expand={expanded === index} /> {group.name?.[i18n.language as 'en' | 'ar']}
                      </p>
                    </AccordionSummary>
                    <AccordionDetails>
                      <div className="specs_list">
                        {group?.specs?.map((item: any) => {
                          const currIndex = ++lastIndex;
                          return (
                            <div className="specs_list_item">
                              <div className="inputs_group">
                                <div className="spec_name" style={{ flex: '1 1' }}>
                                  {item.name?.[i18n.language as 'en' | 'ar']}
                                </div>
                              </div>
                              <div className="inputs_group">
                                <div className="switch_container">
                                  <SwitchActivation
                                    onChange={(value: any) => formik.setFieldValue(`specs[${currIndex}][featured]`, value?.target?.checked ? 1 : 0)}
                                    active={formik.values.specs[currIndex]?.featured}
                                  />
                                  <p>{t('featured')}</p>
                                </div>
                                {item.type === 'text' ? (
                                  <Fragment>
                                    <FieldWrapper
                                      title={t('value_en')}
                                      inputName={`specs[${currIndex}][value][en]`}
                                      inputPlaceholder={t('value_en')}
                                      inputError={formik.errors?.specs as string}
                                      inputTouched={!!formik.errors?.specs as boolean}
                                      input
                                      noPadding
                                      withoutKeyDownBlur
                                    />
                                    <FieldWrapper
                                      title={t('value_ar')}
                                      inputName={`specs[${currIndex}][value][ar]`}
                                      inputPlaceholder={t('value_ar')}
                                      inputError={formik.errors?.specs as string}
                                      inputTouched={!!formik.errors?.specs as boolean}
                                      input
                                      noPadding
                                      withoutKeyDownBlur
                                    />
                                  </Fragment>
                                ) : (
                                  <div className="specs_list_value">
                                    <h5>{t('value')}</h5>
                                    <div className="specs_list_value_options">
                                      <div className="switch_container no_space">
                                        <SwitchActivation
                                          onChange={(value: any) => formik.setFieldValue(`specs[${currIndex}][value]`, value?.target?.checked ? 1 : 0)}
                                          active={formik.values.specs[currIndex]?.value}
                                        />
                                        <p>{t('found')}</p>
                                      </div>
                                      <div className="switch_container no_space">
                                        <SwitchActivation
                                          onChange={(value: any) => formik.setFieldValue(`specs[${currIndex}][value]`, value?.target?.checked ? '' : '')}
                                          active={formik.values.specs[currIndex]?.value === ''}
                                        />
                                        <p>{t('unset')}</p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </div>
              <div className="form_button reverse">
                <Button
                  loading={submitLoading}
                  onClick={() => {
                    // scrollToError(!formik.isValid, formRef);
                  }}
                >
                  <span className="bold">{t('save')}</span>
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </SectionHeader>
  );
}
